import { haversineDistance } from '../../components/map/geo-utils.js'
import { computeFusedElevations } from './elevation-metrics.js'

export interface ProcessingOptions {
  /** Base process noise for Kalman position smoother */
  kalmanBaseQ: number
  /** Scale process noise by median_accuracy / point_accuracy */
  kalmanAccuracyScale: boolean
  /** Floor for GPS accuracy used in measurement noise (meters) */
  kalmanMinAccuracy: number

  /** Window sizes for loop collapse detection */
  loopWindowSizes: number[]
  /** Net/gross ratio below which a window is collapsed */
  loopRatioThreshold: number
  /** Net displacement floor for secondary collapse trigger (meters) */
  loopNetFloor: number
  /** Ratio threshold for floor-based trigger */
  loopFloorRatio: number
  /** Maximum collapse passes */
  loopMaxPasses: number

  /** Minimum rest duration (seconds) */
  restMinDuration: number
  /** Maximum centroid radius for rest cluster (meters) */
  restMaxRadius: number
  /** Speed below which a point can start a rest (m/s) */
  restEntrySpeed: number
  /** Speed below which a point can extend a rest (m/s) */
  restExtendSpeed: number
  /** Minimum point count for a valid rest */
  restMinPoints: number
}

export interface RestSegment {
  startIndex: number
  endIndex: number
  midpoint: [number, number] // [lng, lat] — centroid of rest points
  radius: number // meters — max distance from centroid to any rest point
  startTime: number
  endTime: number
  duration: number // seconds
}

export interface TrackSegment {
  type: 'moving' | 'rest' | 'vehicle'
  startIndex: number
  endIndex: number
}

export interface ProcessedTrack {
  tags: ('moving' | 'rest' | 'vehicle')[]
  smoothedCoords: GeoJSON.Position[]
  cleanedCoords: GeoJSON.Position[]
  cleanedTimestamps: number[]
  cleanedDemElevations: (number | null)[]
  restSegments: RestSegment[]
  segments: TrackSegment[]
  fusedElevations: number[]
  movingTimeMs: number
  elapsedTimeMs: number
  /** Per-cleaned-point uncertainty radius (meters): GPS accuracy + smoothing displacement */
  uncertaintyRadii: number[]
}

const DEFAULT_OPTIONS: ProcessingOptions = {
  kalmanBaseQ: 0.5,
  kalmanAccuracyScale: true,
  kalmanMinAccuracy: 3,

  loopWindowSizes: [5, 7, 10, 15, 20],
  loopRatioThreshold: 0.4,
  loopNetFloor: 5,
  loopFloorRatio: 0.7,
  loopMaxPasses: 3,

  restMinDuration: 60,
  restMaxRadius: 6,
  restEntrySpeed: 0.5,
  restExtendSpeed: 0.8,
  restMinPoints: 3,
}

const DEG_TO_RAD = Math.PI / 180
const METERS_PER_DEG = 111_319.9

export function processTrack(
  coords: GeoJSON.Position[],
  timestamps: number[],
  accuracies: (number | null)[],
  activities: (string | null)[],
  speeds: (number | null)[],
  demElevations: (number | null)[],
  options: Partial<ProcessingOptions> = {}
): ProcessedTrack {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const n = coords.length

  const tags = new Array<'moving' | 'rest' | 'vehicle'>(n).fill('moving')

  filterVehiclePoints(activities, tags)

  const smoothedCoords = smoothPositions(coords, timestamps, accuracies, opts)

  const restSegments = detectRestSegments(smoothedCoords, timestamps, speeds, tags, opts)

  const segments = buildSegments(tags, n)

  const medianAccuracy = computeMedianAccuracy(accuracies)

  const { cleanedCoords, cleanedTimestamps, cleanedDem, cleanedAccuracies, uncertaintyRadii } =
    buildCleanedTrack(
      smoothedCoords,
      coords,
      timestamps,
      demElevations,
      accuracies,
      medianAccuracy,
      tags
    )

  // Cumulative distances for DEM smoothing
  const cumDist: number[] = new Array(cleanedCoords.length).fill(0)
  for (let i = 1; i < cleanedCoords.length; i++) {
    cumDist[i] =
      cumDist[i - 1] +
      haversineDistance(
        cleanedCoords[i - 1][1],
        cleanedCoords[i - 1][0],
        cleanedCoords[i][1],
        cleanedCoords[i][0]
      )
  }

  const smoothedDem = smoothElevationArray(cleanedDem, cumDist)

  const fusedElevations = computeFusedElevations(cleanedCoords, smoothedDem, cleanedAccuracies)

  const elapsedTimeMs = n >= 2 ? timestamps[n - 1] - timestamps[0] : 0
  let movingTimeMs = 0
  for (const seg of segments) {
    if (seg.type === 'moving') {
      movingTimeMs += timestamps[seg.endIndex] - timestamps[seg.startIndex]
    }
  }

  return {
    tags,
    smoothedCoords,
    cleanedCoords,
    cleanedTimestamps,
    cleanedDemElevations: smoothedDem,
    restSegments,
    segments,
    fusedElevations,
    movingTimeMs,
    elapsedTimeMs,
    uncertaintyRadii,
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function computeMedianAccuracy(accuracies: (number | null)[]): number {
  const accValues: number[] = []
  for (const a of accuracies) {
    if (a !== null && a > 0) accValues.push(a)
  }
  accValues.sort((a, b) => a - b)
  if (accValues.length === 0) return 10
  return accValues.length % 2 === 0
    ? (accValues[accValues.length / 2 - 1] + accValues[accValues.length / 2]) / 2
    : accValues[Math.floor(accValues.length / 2)]
}

// ─── Position Smoothing Pipeline ───────────────────────────────────────────

function smoothPositions(
  coords: GeoJSON.Position[],
  timestamps: number[],
  accuracies: (number | null)[],
  opts: ProcessingOptions
): GeoJSON.Position[] {
  const n = coords.length
  if (n < 3) return coords.map(c => [...c])

  const refLon = coords[0][0]
  const refLat = coords[0][1]
  const cosRef = Math.cos(refLat * DEG_TO_RAD)

  const xM = new Float64Array(n)
  const yM = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    xM[i] = (coords[i][0] - refLon) * cosRef * METERS_PER_DEG
    yM[i] = (coords[i][1] - refLat) * METERS_PER_DEG
  }

  const medianAcc = computeMedianAccuracy(accuracies)

  kalmanSmoothAxis(xM, timestamps, accuracies, medianAcc, opts)
  kalmanSmoothAxis(yM, timestamps, accuracies, medianAcc, opts)

  collapseLoops(xM, yM, opts)

  // Smooth GPS elevations — vertical accuracy is ~2.5x worse than horizontal
  const hasElevations = coords.some(c => c.length >= 3)
  let zSmoothed: Float64Array | null = null
  if (hasElevations) {
    zSmoothed = new Float64Array(n)
    for (let i = 0; i < n; i++) {
      zSmoothed[i] = coords[i].length >= 3 ? coords[i][2] : 0
    }
    const vertAccuracies: (number | null)[] = accuracies.map(a => (a !== null ? a * 2.5 : null))
    const vertMedian = medianAcc * 2.5
    kalmanSmoothAxis(zSmoothed, timestamps, vertAccuracies, vertMedian, opts)
  }

  const result: GeoJSON.Position[] = new Array(n)
  for (let i = 0; i < n; i++) {
    const lon = xM[i] / (cosRef * METERS_PER_DEG) + refLon
    const lat = yM[i] / METERS_PER_DEG + refLat
    if (zSmoothed && coords[i].length >= 3) {
      result[i] = [lon, lat, zSmoothed[i]]
    } else {
      result[i] = [lon, lat]
    }
  }
  return result
}

// ─── DEM Elevation Smoothing (distance-weighted moving average) ──────────

function smoothElevationArray(
  elevations: (number | null)[],
  cumDistances: number[],
  windowM: number = 100
): (number | null)[] {
  const n = elevations.length
  if (n < 3) return [...elevations]

  const result: (number | null)[] = new Array(n)
  let lo = 0

  for (let i = 0; i < n; i++) {
    if (elevations[i] === null) {
      result[i] = null
      continue
    }

    const d = cumDistances[i]
    while (lo < i && cumDistances[lo] < d - windowM / 2) lo++
    let hi = i
    while (hi < n - 1 && cumDistances[hi + 1] <= d + windowM / 2) hi++

    let sum = 0,
      count = 0
    for (let k = lo; k <= hi; k++) {
      if (elevations[k] !== null) {
        sum += elevations[k]!
        count++
      }
    }
    result[i] = count > 0 ? sum / count : elevations[i]
  }

  return result
}

// ─── Kalman RTS Smoother (single axis) ─────────────────────────────────────

function kalmanSmoothAxis(
  values: Float64Array,
  timestamps: number[],
  accuracies: (number | null)[],
  medianAcc: number,
  opts: ProcessingOptions
): void {
  const n = values.length
  if (n < 2) return

  const baseQ = opts.kalmanBaseQ
  const scale = opts.kalmanAccuracyScale
  const minAcc = opts.kalmanMinAccuracy

  const fPos = new Float64Array(n)
  const fVel = new Float64Array(n)
  const fP00 = new Float64Array(n)
  const fP01 = new Float64Array(n)
  const fP10 = new Float64Array(n)
  const fP11 = new Float64Array(n)

  fPos[0] = values[0]
  fVel[0] = 0
  fP00[0] = 10
  fP11[0] = 1

  for (let i = 1; i < n; i++) {
    const dt = Math.max((timestamps[i] - timestamps[i - 1]) / 1000, 0.001)
    const acc = accuracies[i] !== null && accuracies[i]! > 0 ? accuracies[i]! : medianAcc
    const qScale = scale ? medianAcc / acc : 1
    const q = baseQ * qScale

    const dt2 = dt * dt
    const Q00 = (q * dt2 * dt) / 3
    const Q01 = (q * dt2) / 2
    const Q11 = q * dt

    const posPred = fPos[i - 1] + fVel[i - 1] * dt
    const P00p = fP00[i - 1] + dt * fP10[i - 1] + dt * fP01[i - 1] + dt2 * fP11[i - 1] + Q00
    const P01p = fP01[i - 1] + dt * fP11[i - 1] + Q01
    const P10p = fP10[i - 1] + dt * fP11[i - 1] + Q01
    const P11p = fP11[i - 1] + Q11

    const r = Math.max(acc, minAcc) ** 2
    const S = P00p + r
    const K0 = P00p / S
    const K1 = P10p / S
    const innov = values[i] - posPred

    fPos[i] = posPred + K0 * innov
    fVel[i] = fVel[i - 1] + K1 * innov
    fP00[i] = (1 - K0) * P00p
    fP01[i] = (1 - K0) * P01p
    fP10[i] = P10p - K1 * P00p
    fP11[i] = P11p - K1 * P01p
  }

  const sPos = new Float64Array(n)
  const sVel = new Float64Array(n)
  sPos[n - 1] = fPos[n - 1]
  sVel[n - 1] = fVel[n - 1]

  for (let i = n - 2; i >= 0; i--) {
    const dt = Math.max((timestamps[i + 1] - timestamps[i]) / 1000, 0.001)
    const acc =
      accuracies[i + 1] !== null && accuracies[i + 1]! > 0 ? accuracies[i + 1]! : medianAcc
    const qScale = scale ? medianAcc / acc : 1
    const q = baseQ * qScale

    const dt2 = dt * dt
    const Q00 = (q * dt2 * dt) / 3
    const Q01 = (q * dt2) / 2
    const Q11 = q * dt

    const Pp00 = fP00[i] + dt * fP10[i] + dt * fP01[i] + dt2 * fP11[i] + Q00
    const Pp01 = fP01[i] + dt * fP11[i] + Q01
    const Pp10 = fP10[i] + dt * fP11[i] + Q01
    const Pp11 = fP11[i] + Q11

    const PFt00 = fP00[i] + fP01[i] * dt
    const PFt01 = fP01[i]
    const PFt10 = fP10[i] + fP11[i] * dt
    const PFt11 = fP11[i]

    const det = Pp00 * Pp11 - Pp01 * Pp10
    const C00 = (PFt00 * Pp11 - PFt01 * Pp10) / det
    const C01 = (-PFt00 * Pp01 + PFt01 * Pp00) / det
    const C10 = (PFt10 * Pp11 - PFt11 * Pp10) / det
    const C11 = (-PFt10 * Pp01 + PFt11 * Pp00) / det

    const d0 = sPos[i + 1] - (fPos[i] + fVel[i] * dt)
    const d1 = sVel[i + 1] - fVel[i]

    sPos[i] = fPos[i] + C00 * d0 + C01 * d1
    sVel[i] = fVel[i] + C10 * d0 + C11 * d1
  }

  values.set(sPos)
}

// ─── Loop Collapse ─────────────────────────────────────────────────────────

function collapseLoops(xM: Float64Array, yM: Float64Array, opts: ProcessingOptions): void {
  const n = xM.length
  const { loopWindowSizes, loopRatioThreshold, loopNetFloor, loopFloorRatio, loopMaxPasses } = opts

  for (let pass = 0; pass < loopMaxPasses; pass++) {
    let collapsed = 0

    for (const ws of loopWindowSizes) {
      let i = 0
      while (i <= n - ws) {
        const dx = xM[i + ws - 1] - xM[i]
        const dy = yM[i + ws - 1] - yM[i]
        const net = Math.sqrt(dx * dx + dy * dy)

        let gross = 0
        for (let j = i; j < i + ws - 1; j++) {
          const sdx = xM[j + 1] - xM[j]
          const sdy = yM[j + 1] - yM[j]
          gross += Math.sqrt(sdx * sdx + sdy * sdy)
        }

        const ratio = gross > 2 ? net / gross : 1
        const ratioTrigger = gross > 2 && ratio < loopRatioThreshold
        const floorTrigger = net < loopNetFloor && ratio < loopFloorRatio && gross > 2

        if (ratioTrigger || floorTrigger) {
          for (let j = 1; j < ws - 1; j++) {
            const t = j / (ws - 1)
            xM[i + j] = xM[i] + t * dx
            yM[i + j] = yM[i] + t * dy
          }
          collapsed++
          i += ws
        } else {
          i++
        }
      }
    }

    if (collapsed === 0) break
  }
}

// ─── Rest Detection (centroid-based) ───────────────────────────────────────

function detectRestSegments(
  coords: GeoJSON.Position[],
  timestamps: number[],
  speeds: (number | null)[],
  tags: ('moving' | 'rest' | 'vehicle')[],
  opts: ProcessingOptions
): RestSegment[] {
  const n = coords.length
  const restSegments: RestSegment[] = []
  let i = 0

  while (i < n) {
    if (tags[i] !== 'moving') {
      i++
      continue
    }

    const isSlow = speeds[i] !== null && speeds[i]! < opts.restEntrySpeed
    if (!isSlow) {
      i++
      continue
    }

    const start = i
    let j = i + 1

    while (j < n) {
      if (tags[j] !== 'moving') {
        j++
        continue
      }

      const canExtend = speeds[j] !== null && speeds[j]! < opts.restExtendSpeed
      if (!canExtend) break

      let cx = 0,
        cy = 0,
        count = 0
      for (let k = start; k <= j; k++) {
        if (tags[k] === 'vehicle') continue
        cx += coords[k][0]
        cy += coords[k][1]
        count++
      }
      if (count === 0) break
      cx /= count
      cy /= count

      let maxDist = 0
      for (let k = start; k <= j; k++) {
        if (tags[k] === 'vehicle') continue
        const d = haversineDistance(coords[k][1], coords[k][0], cy, cx)
        if (d > maxDist) maxDist = d
      }

      if (maxDist >= opts.restMaxRadius) break

      j++
    }

    const duration = (timestamps[j - 1] - timestamps[start]) / 1000
    const pointCount = j - start

    if (duration >= opts.restMinDuration && pointCount >= opts.restMinPoints) {
      let cx = 0,
        cy = 0,
        count = 0
      for (let k = start; k < j; k++) {
        if (tags[k] === 'vehicle') continue
        tags[k] = 'rest'
        cx += coords[k][0]
        cy += coords[k][1]
        count++
      }
      cx /= count
      cy /= count

      let maxDist = 0
      for (let k = start; k < j; k++) {
        if (tags[k] !== 'rest') continue
        const d = haversineDistance(coords[k][1], coords[k][0], cy, cx)
        if (d > maxDist) maxDist = d
      }

      restSegments.push({
        startIndex: start,
        endIndex: j - 1,
        midpoint: [cx, cy],
        radius: maxDist,
        startTime: timestamps[start],
        endTime: timestamps[j - 1],
        duration,
      })

      i = j
    } else {
      i = j
    }
  }

  return restSegments
}

// ─── Vehicle Filtering ─────────────────────────────────────────────────────

function filterVehiclePoints(
  activities: (string | null)[],
  tags: ('moving' | 'rest' | 'vehicle')[]
): void {
  for (let i = 0; i < activities.length; i++) {
    if (activities[i] === 'vehicle') {
      tags[i] = 'vehicle'
    }
  }
}

// ─── Cleaned Track Building ────────────────────────────────────────────────

function buildCleanedTrack(
  smoothedCoords: GeoJSON.Position[],
  rawCoords: GeoJSON.Position[],
  timestamps: number[],
  demElevations: (number | null)[],
  accuracies: (number | null)[],
  medianAccuracy: number,
  tags: ('moving' | 'rest' | 'vehicle')[]
): {
  cleanedCoords: GeoJSON.Position[]
  cleanedTimestamps: number[]
  cleanedDem: (number | null)[]
  cleanedAccuracies: (number | null)[]
  uncertaintyRadii: number[]
} {
  const cleanedCoords: GeoJSON.Position[] = []
  const cleanedTimestamps: number[] = []
  const cleanedDem: (number | null)[] = []
  const cleanedAccuracies: (number | null)[] = []
  const uncertaintyRadii: number[] = []

  for (let i = 0; i < smoothedCoords.length; i++) {
    if (tags[i] === 'vehicle') continue
    cleanedCoords.push(smoothedCoords[i])
    cleanedTimestamps.push(timestamps[i])
    cleanedDem.push(demElevations[i])
    cleanedAccuracies.push(accuracies[i])

    const displacement = haversineDistance(
      rawCoords[i][1],
      rawCoords[i][0],
      smoothedCoords[i][1],
      smoothedCoords[i][0]
    )
    const accuracy = accuracies[i] ?? medianAccuracy
    uncertaintyRadii.push(displacement + accuracy)
  }

  return { cleanedCoords, cleanedTimestamps, cleanedDem, cleanedAccuracies, uncertaintyRadii }
}

// ─── Segment Building ──────────────────────────────────────────────────────

function buildSegments(tags: ('moving' | 'rest' | 'vehicle')[], n: number): TrackSegment[] {
  if (n === 0) return []
  const segments: TrackSegment[] = []
  let currentType = tags[0]
  let startIndex = 0

  for (let i = 1; i < n; i++) {
    if (tags[i] !== currentType) {
      segments.push({ type: currentType, startIndex, endIndex: i - 1 })
      currentType = tags[i]
      startIndex = i
    }
  }
  segments.push({ type: currentType, startIndex, endIndex: n - 1 })
  return segments
}
